// The Ninja cutpurse. Only ever exists as a REAL thief: when one stalks you the scribe says so, when it
// catches you it takes a real, capped share of this trip's pending coin, and sprinting outruns it.
import * as THREE from 'three'
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { CapitalConfig as CFG, ThiefConfig as T } from '../config/capital.js'
import { record, debit, pendingNet, isOnTrip } from '../game/capitalTrip.js'
import { formatCoins } from '../ui/format.js'

const WALK_CYCLE_SPEED = 1.5

export function createThief({ parent, assets, stepM }) {
  if (!assets?.ninja) return { tick() {}, reset() {}, positions: () => [], active: () => false }
  const { ninja, textures } = assets
  const walkClip = ninja.animations.find((c) => c.name.toLowerCase() === 'walk')
  const idleClip = ninja.animations.find((c) => c.name.toLowerCase() === 'idle')
  const mat = new THREE.MeshLambertMaterial({ map: textures[0], side: THREE.DoubleSide })
  mat.color.setScalar(CFG.brightness)

  let holder = null, mixer = null
  let st = 'none', since = 0, heading = 0, farSince = 0
  let tripStart = 0, count = 0, lastEnd = 0, qualifiedAt = 0

  function spawnAt(p) {
    holder = new THREE.Group()
    const model = cloneSkinned(ninja.scene)
    model.scale.setScalar(CFG.npcScale)
    model.traverse((o) => { if (o.isMesh || o.isSkinnedMesh) { o.material = mat; o.frustumCulled = false } })
    holder.add(model)
    holder.position.set(p.x, p.y, p.z)
    parent.add(holder)
    mixer = new THREE.AnimationMixer(model)
    const w = walkClip && mixer.clipAction(walkClip)
    w?.setEffectiveTimeScale(T.speed / WALK_CYCLE_SPEED)
    w?.play()
  }
  function remove() {
    if (holder) parent.remove(holder)
    holder = null; mixer = null; st = 'none'; lastEnd = Date.now()
  }
  function reset() { remove(); count = 0; tripStart = Date.now(); qualifiedAt = 0; lastEnd = 0 }

  function findSpawn(hx, hz, hy) {
    for (let i = 0; i < 40; i++) {
      const a = Math.random() * Math.PI * 2, r = T.spawnMin + Math.random() * (T.spawnMax - T.spawnMin)
      const x = hx + Math.cos(a) * r, z = hz + Math.sin(a) * r
      const y = stepM(x, z, hy)
      if (y !== null && Math.abs(y - hy) < 2.5) return { x, y, z }
    }
    return null
  }

  function moveToward(tx, tz, speed, dt) {
    const h = holder.position
    const dx = tx - h.x, dz = tz - h.z, d = Math.hypot(dx, dz) || 1
    const step = Math.min(speed * dt, d)
    const tryMove = (nx, nz) => { const ny = stepM(nx, nz, h.y); if (ny === null) return false; h.x = nx; h.z = nz; h.y += (ny - h.y) * Math.min(1, 14 * dt); return true }
    if (!tryMove(h.x + (dx / d) * step, h.z + (dz / d) * step)) {
      if (!tryMove(h.x + Math.sign(dx) * step, h.z)) tryMove(h.x, h.z + Math.sign(dz) * step)
    }
    const want = Math.atan2(dx, dz)
    let diff = Math.atan2(Math.sin(want - heading), Math.cos(want - heading))
    heading += diff * Math.min(1, 9 * dt); holder.rotation.y = heading
  }

  // hx,hz,hy: hero position in source metres.
  function tick(dt, hx, hz, hy) {
    if (!isOnTrip()) { if (holder) remove(); return }
    if (!tripStart) tripStart = Date.now()
    const now = Date.now()
    mixer?.update(dt)

    if (st === 'none') {
      if (count >= T.maxPerTrip || now - lastEnd < T.cooldownMs) return
      if (pendingNet() < T.minNet) { qualifiedAt = 0; return }
      if (!qualifiedAt) qualifiedAt = now
      if (now - qualifiedAt < T.firstDelayMs) return
      const p = findSpawn(hx, hz, hy)
      if (!p) return
      spawnAt(p); st = 'chase'; since = now; farSince = 0; count++
      record({ kind: 'info', source: 'thief', severity: 'smoke', text: `A cutpurse is stalking you — you carry ${formatCoins(pendingNet())} this trip. Sprint (Shift) to lose them.` })
      return
    }
    const h = holder.position
    const d = Math.hypot(hx - h.x, hz - h.z)
    if (st === 'chase') {
      moveToward(hx, hz, T.speed, dt)
      if (d <= T.catchM && Math.abs(hy - h.y) < 2.5) {
        const net = pendingNet()
        const take = Math.min(T.stealCap, Math.max(T.stealMin, Math.round(net * T.stealFrac)))
        if (net < T.stealMin) record({ kind: 'info', source: 'thief', text: 'The cutpurse patted you down and found nothing worth taking.' })
        else debit(take, `A cutpurse took ${formatCoins(take)} from your trip earnings`, 'thief', 'banner')
        st = 'flee'; since = now; return
      }
      farSince = d > T.giveUpDist ? (farSince || now) : 0
      if ((farSince && now - farSince > T.giveUpMs) || now - since > T.chaseMaxMs) {
        record({ kind: 'info', source: 'thief', text: 'The cutpurse lost your trail.' })
        remove()
      }
    } else if (st === 'flee') {
      moveToward(h.x + (h.x - hx), h.z + (h.z - hz), T.speed, dt)
      if (now - since > T.fleeMs) remove()
    }
  }

  return {
    tick, reset,
    positions: () => (holder ? [{ x: holder.position.x, z: holder.position.z, kind: 'thief' }] : []),
    active: () => st !== 'none',
    debug: () => ({ st, count, lastEnd, qualifiedAt, tripStart, net: pendingNet(), on: isOnTrip() }),
  }
}
